export function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
}

export function openInNewTab(url: string) {
    window.open(url, '_blank');
}

export function isTerminalPollingError(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return false;
    const candidate = error as {
        statusCode?: unknown;
        status?: unknown;
        response?: { status?: unknown };
        data?: { statusCode?: unknown };
    };
    const status = typeof candidate.statusCode === 'number'
        ? candidate.statusCode
        : typeof candidate.status === 'number'
            ? candidate.status
            : typeof candidate.response?.status === 'number'
                ? candidate.response.status
                : typeof candidate.data?.statusCode === 'number'
                    ? candidate.data.statusCode
                    : undefined;
    return status === 403 || status === 404;
}

export function usePageInterval(callback: () => unknown, config: {
    interval: number,
    immediate?: boolean,
} = { interval: 1000, immediate: true }) {
    let stop = false;
    let terminalStop = false;
    let timeoutHandler: NodeJS.Timeout | null = null;
    // Keep the active request across stop/resume so reactivation cannot start a second loop.
    let inFlight: Promise<void> | null = null;
    const stopPolling = () => {
        stop = true;
        if (timeoutHandler) {
            clearTimeout(timeoutHandler);
            timeoutHandler = null;
        }
    };

    const stopPermanently = () => {
        terminalStop = true;
        stopPolling();
    };

    const trigger = (): Promise<void> => {
        if (stop || terminalStop) return Promise.resolve();
        if (inFlight) return inFlight;
        if (timeoutHandler) {
            clearTimeout(timeoutHandler);
            timeoutHandler = null;
        }
        inFlight = Promise.resolve().then(callback).then(() => { }, (error) => {
            if (isTerminalPollingError(error)) {
                stopPermanently();
            }
        }).finally(() => {
            inFlight = null;
            if (!stop && !terminalStop) timeoutHandler = setTimeout(trigger, config.interval);
        });
        return inFlight;
    }

    onMounted(() => {
        if (terminalStop) return;
        if (config.immediate) trigger();
        else timeoutHandler = setTimeout(trigger, config.interval);
    })

    // Stop polling immediately when navigating away — fires BEFORE route
    // middleware, so the auth session refresh isn't blocked by in-flight polls.
    onBeforeRouteLeave(stopPolling);

    // Stop polling when keepalive component is deactivated (e.g. switching tabs)
    onDeactivated(stopPolling);

    // Resume polling when keepalive component is reactivated
    onActivated(() => {
        if (stop && !terminalStop) {
            stop = false;
            trigger();
        }
    });

    // Final cleanup on unmount
    onUnmounted(stopPolling);

    return Object.assign(trigger, { stop: stopPermanently });
}

export function formatCompactNumber(count: number) {
    if (!count) return 'Unknown';
    if (count < 1000) {
        return count;
    }
    if (count < 1_000_000) {
        return `${(count / 1_000).toFixed(1)}k`;
    }
    return `${(count / 1_000_000).toFixed(1)}M`;
}